import * as React from 'react';
import { Form, DatePickerRange, TextInput, Select, CustomSubmitButton, Checkbox, ClearFormButton, CustomSubmitButtonType } from "soprasteria-ui-library"
import { useAuth } from '../../../App';
import { ReferralSearchApi } from '../../../api/ReferralSearchApi';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { CareProviderDropdown } from '../../CareProviderDropdown/CareProviderDropdown';
import { ICareProvider } from '../../../models/ICareProvider';
import { IReferralSearchParams } from '../../../models/IReferral';
import { UserContext, isInRole } from '../../../contexts/UserContext';
import { ALLMÄNTANDVÅRDBAS, SPECIALISTTANDVÅRDBAS, KÄKORTOPEDIBAS, VÅRDANSVARIG, TANDREGLERINGBAS, LIBRETTOADMIN } from '../../../utils/RoleConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';

interface IReferralSearchForm {
    preSetSearchParams?: IReferralSearchParams;
    searchReferrals: (data: IReferralSearchParams) => void;
}

export const ReferralSearchForm: React.FunctionComponent<IReferralSearchForm> = ({ searchReferrals, preSetSearchParams }) => {
    const auth = useAuth();
    const api = new ReferralSearchApi(auth);
    const [referralStatuses, setReferralStatuses] = React.useState<IListItem[]>()

    const [referrerUmbrellaOrganisations, setReferrerUmbrellaOrganisations] = React.useState<ICareProvider[]>();
    const [referrerCareProviders, setReferrerCareProviders] = React.useState<ICareProvider[]>();
    const [resetReferrerCareProviderValue, doResetReferrerCareProviderValue] = React.useState<boolean>(false);
    const [isFetchingReferrerUmbrellaOrganisations, setIsFetchingReferrerUmbrellaOrganisations] = React.useState<boolean>(false);
    const [selectedReferrerUmbrellaOrganisationId, setSelectedReferrerUmbrellaOrganisationId] = React.useState<number>();
    const [selectedRecipientUmbrellaOrganisationId, setSelectedRecipientUmbrellaOrganisationId] = React.useState<number>();
    const [recipientUmbrellaOrganisations, setRecipientUmbrellaOrganisations] = React.useState<ICareProvider[]>();
    const [recipientCareProviders, setRecipientCareProviders] = React.useState<ICareProvider[]>();
    const [resetRecipientCareProviderValue, doResetRecipientCareProviderValue] = React.useState<boolean>(false);
    const [isFetchingRecipientUmbrellaOrganisations, setIsFetchingRecipientUmbrellaOrganisations] = React.useState<boolean>(false);
    const [onlyShowReferralsInNeedOfProcessing, setOnlyShowReferralsInNeedOfProcessing] = React.useState<boolean>(false);
    const [onlyShowReferralsWithUnreadAnswer, setOnlyShowReferralsWithUnreadAnswer] = React.useState<boolean>(false);
    const [showReferralsToShare, setShowReferralsToShare] = React.useState<boolean>(false);
    const userInfo = React.useContext(UserContext);
    const hasRoleSystemAdministrator = isInRole(LIBRETTOADMIN, userInfo.roles);
    const hasRoleAllmantandvard = isInRole(ALLMÄNTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleSpecialisttandvard = isInRole(SPECIALISTTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles ? userInfo.roles : []);

    const abortController = new AbortController();
    const [isExportingToExcel, setIsExportingToExcel] = React.useState<boolean>(false);

    React.useEffect(() => {
        getReferralStatuses();
        getReferrerUmbrellaOrganisations();
        getRecipientUmbrellaOrganisations();

        return () => {
            abortController.abort();
        }
    }, [])

    React.useEffect(() => {
        if (preSetSearchParams?.onlyShowReferralsInNeedOfProcessing) {
            setOnlyShowReferralsInNeedOfProcessing(true);
        }
        if (preSetSearchParams?.showReferralsToShare) {
            setShowReferralsToShare(true);
        }
        if (preSetSearchParams?.onlyShowReferralsWithUnreadAnswer) {
            setOnlyShowReferralsWithUnreadAnswer(true);
        }
    }, [preSetSearchParams])

    const getReferralStatuses = () => {
        api.getReferralStatuses((statuses) => {
            const statusListItems = statuses.map<IListItem>((status) => ({ text: status.description, value: status.statusId.toString() }));
            setReferralStatuses(statusListItems);
        });
    }

    const getReferrerUmbrellaOrganisations = () => {
        setIsFetchingReferrerUmbrellaOrganisations(true);
        api.getReferrerUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaOrganisationIdToUse;
            if (preSetSearchParams?.referrerUmbrellaOrganisationId) {
                umbrellaOrganisationIdToUse = parseInt(preSetSearchParams.referrerUmbrellaOrganisationId.toString());
                getReferrerCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            } else if (!hasRoleSystemAdministrator) {
                if (!hasRoleSpecialisttandvard) {
                    umbrellaOrganisationIdToUse = umbrellaOrganisations[0].careProviderId;
                    setSelectedReferrerUmbrellaOrganisationId(umbrellaOrganisations[0].careProviderId);
                    getReferrerCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
                }

            }
            setReferrerUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingReferrerUmbrellaOrganisations(false);
        });
    }

    const getReferrerCareProvidersByUmbrellaOrganisationId = (umbrellaOrganisationId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingReferrerUmbrellaOrganisations(true);

        api.getReferrerCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationId, (careProviders) => {
            if (careProviders.length !== 0) {
                setReferrerCareProviders(careProviders);
            } else {
                setReferrerCareProviders(undefined);
            }
            setIsFetchingReferrerUmbrellaOrganisations(false);
        }, undefined, () => { });
    }

    const onValueChangeReferrerUmbrellaOrganisations = (selectedUmbrellaOrganisation: string | undefined) => {
        if (selectedUmbrellaOrganisation && selectedUmbrellaOrganisation !== "") {
            getReferrerCareProvidersByUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
            setSelectedReferrerUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
        }
        else {
            setSelectedReferrerUmbrellaOrganisationId(undefined);
            setReferrerCareProviders([]);
        }
        doResetReferrerCareProviderValue(prev => !prev);
    }

    const getReferrerUmbrellaOrganisationDefaultValue = () => {
        if (referrerUmbrellaOrganisations && preSetSearchParams?.referrerUmbrellaOrganisationId) {
            return preSetSearchParams.referrerUmbrellaOrganisationId.toString();
        }

        if (selectedReferrerUmbrellaOrganisationId) {
            return selectedReferrerUmbrellaOrganisationId.toString();
        }

        if (referrerUmbrellaOrganisations?.length === 1) {
            return referrerUmbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getReferrerCareProviderDefaultValue = () => {
        if (referrerCareProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.referrerCareProviderId
                ? parseInt(preSetSearchParams.referrerCareProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? referrerCareProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (referrerCareProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.referrerCareProviderId?.toString();
            }

            if (referrerCareProviders && referrerCareProviders.length === 1 && referrerCareProviders[0] !== undefined) {
                return referrerCareProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    const getRecipientUmbrellaOrganisations = () => {
        setIsFetchingRecipientUmbrellaOrganisations(true);
        api.getRecipientUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaOrganisationIdToUse;
            if (preSetSearchParams?.recipientUmbrellaOrganisationId) {
                umbrellaOrganisationIdToUse = parseInt(preSetSearchParams?.recipientUmbrellaOrganisationId.toString());
                setSelectedRecipientUmbrellaOrganisationId(umbrellaOrganisationIdToUse)

                getRecipientCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            }

            setRecipientUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingRecipientUmbrellaOrganisations(false);
        });
    }

    const getRecipientCareProvidersByUmbrellaOrganisationId = (umbrellaOrganisationId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingRecipientUmbrellaOrganisations(true);

        api.getRecipientCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationId, (careProviders) => {
            if (careProviders.length !== 0) {
                setRecipientCareProviders(careProviders);
            } else {
                setRecipientCareProviders(undefined);
            }
            setIsFetchingRecipientUmbrellaOrganisations(false);
        }, undefined, () => { });
    }

    const onValueChangeRecipientUmbrellaOrganisations = (selectedUmbrellaOrganisation: string | undefined) => {
        if (selectedUmbrellaOrganisation && selectedUmbrellaOrganisation !== "") {
            getRecipientCareProvidersByUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
            setSelectedRecipientUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
        }
        else {
            setSelectedRecipientUmbrellaOrganisationId(undefined);
            setRecipientCareProviders([]);
        }
        doResetRecipientCareProviderValue(prev => !prev);
    }

    const getRecipientUmbrellaOrganisationDefaultValue = () => {
        if (recipientUmbrellaOrganisations && preSetSearchParams?.recipientUmbrellaOrganisationId) {
            return preSetSearchParams.recipientUmbrellaOrganisationId.toString();
        }

        if (selectedRecipientUmbrellaOrganisationId) {
            return selectedRecipientUmbrellaOrganisationId.toString();
        }

        if (recipientUmbrellaOrganisations?.length === 1) {
            return recipientUmbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getRecipientCareProviderDefaultValue = () => {
        if (recipientCareProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.recipientCareProviderId
                ? parseInt(preSetSearchParams.recipientCareProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? recipientCareProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (recipientCareProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.recipientCareProviderId?.toString();
            }

            if (recipientCareProviders && recipientCareProviders.length === 1 && recipientCareProviders[0] !== undefined) {
                return recipientCareProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    const exportSearchResultToExcel = (data: IReferralSearchParams) => {
        setIsExportingToExcel(true);

        if (hasRoleSpecialisttandvard && data.includeArchivedReferrals === undefined) {
            data.includeArchivedReferrals = false;
        }
        api.searchReferrals(data, true, undefined, "Specialistremisser", undefined, () => setIsExportingToExcel(false));
    }

    return (
        <section>
            <Form >
                <div className="row">
                    <div className="col-6">
                        <DatePickerRange
                            label="Datum"
                            name="dateFrom"
                            nameSecondary="dateTo"
                            inlineLabel
                            value={preSetSearchParams?.dateFrom ? new Date(preSetSearchParams.dateFrom) : undefined}
                            valueSecondary={preSetSearchParams?.dateTo ? new Date(preSetSearchParams.dateTo) : undefined} />
                        <TextInput name="referralNumber" label="Remiss-ID" defaultValue={preSetSearchParams?.referralNumber} inlineLabel />
                        <TextInput name="externalId" label="Externt remiss-ID" defaultValue={preSetSearchParams?.externalId} inlineLabel />
                        <Select name="referralStatusId" label="Status" placeholder="Välj en status"
                            inlineLabel isLoading={referralStatuses === undefined}
                            options={referralStatuses ?? []}
                            selectedValue={preSetSearchParams?.referralStatusId?.toString()}
                            isClearable disabled={onlyShowReferralsInNeedOfProcessing} />
                        {
                            hasRoleAllmantandvard ?
                                <>
                                    <Checkbox name="showReferreralsWherePatientIsNotListedOnCareProvider" id="showReferreralsWherePatientIsNotListedOnCareProvider"
                                        label={!hasRoleParaply ? "Visa specialistremisser där patient inte längre är listad på mottagningen" : "Visa aktiva specialistremisser där patient inte är listad på någon av mina vårdgivare"}
                                        checked={preSetSearchParams?.showReferreralsWherePatientIsNotListedOnCareProvider ?? false} value="true" onChange={(val) => setShowReferralsToShare(val)} />

                                </> : null

                        }
                        {
                            showReferralsToShare && !hasRoleParaply ?
                                <>
                                    <Checkbox name="showReferralsToShare" id="showReferralsToShare" label="Visa specialistremisser som kan delas"
                                        checked={preSetSearchParams?.showReferralsToShare ?? false} value="true" />

                                </> : null
                        }
                    </div>
                    <div className="col-6">
                        <CareProviderDropdown
                            name="recipientUmbrellaOrganisationId"
                            label="Mottagande vårdgivare"
                            placeholder="Välj vårdgivare"
                            isLoading={isFetchingRecipientUmbrellaOrganisations}
                            onValueChange={onValueChangeRecipientUmbrellaOrganisations}
                            careProviders={recipientUmbrellaOrganisations}
                            defaultValue={getRecipientUmbrellaOrganisationDefaultValue()}
                            isClearable={recipientUmbrellaOrganisations?.length !== 1}
                            showOrgNum
                        />
                        <CareProviderDropdown
                            name="recipientCareProviderId"
                            isLoading={recipientCareProviders === undefined}
                            label={"Mottagande mottagning"}
                            careProviders={recipientCareProviders ?? []}
                            defaultValue={getRecipientCareProviderDefaultValue()}
                            isClearable={recipientCareProviders?.length !== 1}
                            showClinicId
                            resetValue={resetRecipientCareProviderValue}
                        />
                        <CareProviderDropdown
                            label="Remittent vårdgivare"
                            name="referrerUmbrellaOrganisationId"
                            placeholder="Välj vårdgivare"
                            isLoading={isFetchingReferrerUmbrellaOrganisations}
                            careProviders={referrerUmbrellaOrganisations}
                            onValueChange={onValueChangeReferrerUmbrellaOrganisations}
                            defaultValue={getReferrerUmbrellaOrganisationDefaultValue()}
                            isClearable={referrerUmbrellaOrganisations?.length !== 1}
                            showOrgNum
                        />
                        <CareProviderDropdown
                            label={"Remittent mottagning"}
                            name="referrerCareProviderId"
                            isLoading={referrerCareProviders === undefined}
                            careProviders={referrerCareProviders}
                            defaultValue={getReferrerCareProviderDefaultValue()}
                            isClearable={referrerCareProviders?.length !== 1}
                            showClinicId
                            resetValue={resetReferrerCareProviderValue}
                        />
                        <Checkbox name="includeArchivedReferrals" id="includeArchivedReferrals" label="Visa avslutade specialistremisser äldre än tre månader" checked={preSetSearchParams?.includeArchivedReferrals ?? false} value="true" />
                        {
                            hasRoleAllmantandvard || hasRoleSpecialisttandvard ?
                                <Checkbox name="onlyShowReferralsWithUnreadAnswer" id="onlyShowReferralsWithUnreadAnswer" label="Visa bara specialistremisser med remissvar ej läst" value="true"
                                    checked={onlyShowReferralsWithUnreadAnswer} onChange={(changedValue) => setOnlyShowReferralsWithUnreadAnswer(changedValue)} />
                                : null
                        }
                        {
                            hasRoleSpecialisttandvard ?
                                <Checkbox name="onlyShowReferralsInNeedOfProcessing" label="Visa bara specialistremisser med för lång handläggningstid" value="true"
                                    checked={onlyShowReferralsInNeedOfProcessing} onChange={(changedValue) => setOnlyShowReferralsInNeedOfProcessing(changedValue)} />
                                : null
                        }
                    </div>
                    <div className="col-12">
                        <div className="text-right panel">
                            <span className="d-none">
                                <CustomSubmitButton onButtonSubmit={searchReferrals}>Sök</CustomSubmitButton>
                            </span>
                            <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} onButtonSubmit={exportSearchResultToExcel} isLoading={isExportingToExcel}><FontAwesomeIcon icon={faFileExcel} /> Exportera till excel</CustomSubmitButton>
                            <ClearFormButton />
                            <CustomSubmitButton onButtonSubmit={searchReferrals}>Sök</CustomSubmitButton>
                        </div>
                    </div>
                </div>
            </Form>
        </section>
    )
}